import { useEffect, useRef, useState, type ReactNode } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { cn } from './cn';

/**
 * A page's secondary actions: in line on a desktop, behind one "More" button
 * on a phone.
 *
 * A heading with Refresh, Import, Export and the page's real action wrapped
 * onto three rows on a phone, and the one a person came for was no easier to
 * find than the rest. Wrap everything except the main action in this. Each
 * child keeps its own handler and its own permission gate — nothing is
 * removed, it is one tap further away. From `md` up both wrappers are
 * `display: contents`, so the buttons sit in the heading exactly as they did.
 *
 * Children should carry their name in words: an icon alone in a menu is a
 * guess.
 */
export function MoreActions({ children, label = 'More', align = 'left', compact = false, className }: {
  children: ReactNode;
  label?: string;
  /**
   * The button as a 40px "⋯" alone, for a menu on every row of a list where
   * the word would not fit beside the row's own action. The menu's items
   * are still named; the button says "More actions" to a screen reader.
   */
  compact?: boolean;
  /** Which edge of the button the menu hangs from. `right` for a button at the end of a row. */
  align?: 'left' | 'right';
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e: MouseEvent) => { if (!root.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);

  return (
    <div ref={root} className={cn('relative md:contents', className)}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        aria-label={compact ? `${label} actions` : undefined}
        className={cn(
          'inline-flex h-10 items-center justify-center gap-1.5 rounded-btn border border-neutral-300 bg-white text-small font-semibold text-neutral-800 md:hidden',
          compact ? 'w-10' : 'px-3',
        )}
      >
        <MoreHorizontal aria-hidden className="h-4 w-4" />
        {!compact && label}
      </button>
      <div
        // Choosing something closes the menu; the child's own handler has run by then.
        onClick={() => setOpen(false)}
        className={cn(
          open ? 'flex' : 'hidden',
          'absolute top-full z-30 mt-1 min-w-[13rem] flex-col gap-1 rounded-card border border-neutral-200 bg-white p-2 shadow-dropdown',
          align === 'right' ? 'right-0' : 'left-0',
          'max-md:[&>*]:w-full max-md:[&>*]:justify-start',
          'md:contents',
        )}
      >
        {children}
      </div>
    </div>
  );
}
